import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  PatchAdminV1OrgMembersIdBody,
  PostAdminV1OrgMembersInvitationsBody,
} from '@nucleus-commerce/api-client/admin';

import { teamApi } from '../api/team-api';

import { teamKeys } from './team-keys';

/** Every change refetches the list: the backend decides role names, statuses and ordering. */
export function useInviteMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1OrgMembersInvitationsBody) => teamApi.invite(body),
    meta: { successMessage: 'team:toasts.invited', silentError: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: teamKeys.members() }),
  });
}

export function useResendInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => teamApi.resendInvitation(id),
    meta: { successMessage: 'team:toasts.resent', silentError: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: teamKeys.members() }),
  });
}

export function useUpdateMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchAdminV1OrgMembersIdBody }) =>
      teamApi.update(id, body),
    meta: { successMessage: 'team:toasts.updated', silentError: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: teamKeys.members() }),
  });
}

export function useRemoveMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => teamApi.remove(id),
    meta: { successMessage: 'team:toasts.removed', silentError: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: teamKeys.members() }),
  });
}
